/**
 * Les appels métier, nommés comme le domaine.
 *
 * Une seule couche au-dessus du client HTTP, pour que les vues ne manipulent ni
 * chemins ni verbes. Les écritures qui doivent survivre à une coupure réseau
 * passent `differable: true` : cocher une tâche dans le métro doit marcher.
 */

import type {
  Document,
  Equipement,
  Famille,
  Logement,
  PeriodeVacances,
  Personne,
  Prestataire,
  Realisation,
  RegleDeduite,
  Relance,
  Tache,
  TypeEquipement,
  Utilisateur,
} from '@foyer/noyau';

import { MODE_JETON, ecrit, lit, memoriseJeton } from './api.js';

export interface Simulation {
  type?: { cle: string; nom: string; categorie: string; icone: string };
  regles: RegleDeduite[];
  avertissements: string[];
  avertissement?: string;
}

export interface Application {
  creees: Tache[];
  misesAJour: { tache: { id: string } }[];
  ignorees: { modeleCle: string; titre: string; motif: string }[];
  avertissements?: string[];
}

export const donnees = {
  // ── Session ─────────────────────────────────────────────────────────────
  moi: () => lit<{ utilisateur: Utilisateur; famille: Famille }>('/auth/moi'),
  connexion: async (email: string, motDePasse: string) => {
    // `avecJeton` ne change rien sur le web : le serveur ne renvoie le jeton que
    // si on le demande, et seul le mobile le demande.
    const r = await ecrit<{ utilisateur: Utilisateur; famille: Famille; jeton?: string }>(
      'POST',
      '/auth/connexion',
      { email, motDePasse, avecJeton: MODE_JETON },
    );
    memoriseJeton(r.jeton);
    return r;
  },
  inscription: async (corps: { email: string; motDePasse: string; prenom: string; nomFoyer?: string }) => {
    const r = await ecrit<{ utilisateur: Utilisateur; famille: Famille; jeton?: string }>(
      'POST',
      '/auth/inscription',
      { ...corps, avecJeton: MODE_JETON },
    );
    memoriseJeton(r.jeton);
    return r;
  },
  rejoindre: async (corps: {
    email: string;
    motDePasse: string;
    prenom: string;
    codeInvitation: string;
    role?: 'parent' | 'enfant';
  }) => {
    const r = await ecrit<{ utilisateur: Utilisateur; jeton?: string }>('POST', '/auth/rejoindre', {
      ...corps,
      avecJeton: MODE_JETON,
    });
    memoriseJeton(r.jeton);
    return r;
  },
  /**
   * Connexion par compte Google.
   *
   * Le second argument sert au cas « adresse inconnue » : le serveur répond
   * alors 404 avec `inconnu: true`, l'écran demande un code d'invitation ou un
   * nom de foyer, et rappelle cette même fonction avec le MÊME jeton Google.
   * Redemander un jeton à Google entre-temps ferait réapparaître le sélecteur de
   * compte, ce qui donne l'impression que la première tentative a échoué.
   */
  connexionGoogle: async (
    jetonId: string,
    ou: {
      codeInvitation?: string;
      nomFoyer?: string;
      creerFoyer?: boolean;
      prenom?: string;
      /**
       * Forcer le jeton porteur alors qu'on tourne sur le web. Un seul appelant :
       * le site ouvert par l'application Android dans un onglet de navigateur,
       * qui doit récupérer le jeton pour le lui renvoyer par lien profond.
       */
      avecJeton?: boolean;
    } = {},
  ) => {
    const { avecJeton = MODE_JETON, ...reste } = ou;
    const r = await ecrit<{ utilisateur: Utilisateur; famille: Famille; jeton?: string }>(
      'POST',
      '/auth/google',
      { jetonId, ...reste, avecJeton },
    );
    memoriseJeton(r.jeton);
    return r;
  },

  /**
   * Le serveur accepte-t-il la connexion Google ? Route publique, sans session.
   *
   * Sous `/auth/` et non `/health` : nginx ne proxifie que `/api/` et
   * `/auth/` vers l'API, et `/health` depuis le navigateur ne rendrait que
   * l'index HTML de la PWA.
   */
  etatServeur: () => lit<{ google: boolean }>('/auth/etat'),

  deconnexion: () => ecrit<{ deconnecte: boolean }>('POST', '/auth/deconnexion'),

  // ── Foyer ───────────────────────────────────────────────────────────────
  foyer: () => lit<{ famille: Famille; membres: Utilisateur[] }>('/api/foyer'),
  majFoyer: (corps: Record<string, unknown>) => ecrit<Famille>('PATCH', '/api/foyer', corps),
  zones: () => lit<{ zones: string[] }>('/api/foyer/zones'),
  nouveauCode: () => ecrit<{ codeInvitation: string }>('POST', '/api/foyer/code-invitation'),
  nouveauJetonIcal: () => ecrit<{ jetonIcal: string }>('POST', '/api/foyer/jeton-ical'),

  // ── Personnes ───────────────────────────────────────────────────────────
  personnes: () => lit<{ personnes: Personne[] }>('/api/personnes'),
  personne: (id: string) =>
    lit<{ personne: Personne; taches: Tache[]; documents: Document[] }>(`/api/personnes/${id}`),
  creePersonne: (corps: Record<string, unknown>) =>
    ecrit<{ personne: Personne } & Application>('POST', '/api/personnes', corps),
  majPersonne: (id: string, corps: Record<string, unknown>) =>
    ecrit<{ personne: Personne }>('PATCH', `/api/personnes/${id}`, corps),
  supprimePersonne: (id: string) => ecrit<{ supprime: boolean }>('DELETE', `/api/personnes/${id}`),
  regenereSante: (id: string) => ecrit<Application>('POST', `/api/personnes/${id}/sante`, {}),

  // ── Maison ──────────────────────────────────────────────────────────────
  catalogues: () =>
    lit<{
      equipements: { types: TypeEquipement[] };
      sante: { avertissement: string };
      administratif: { modeles: { cle: string; titre: string; parametre: string }[] };
      ecole: { regles: { cle: string; titre: string }[] };
    }>('/api/catalogues'),
  equipements: () => lit<{ equipements: Equipement[] }>('/api/equipements'),
  equipement: (id: string) =>
    lit<{ equipement: Equipement; taches: Tache[]; documents: Document[] }>(`/api/equipements/${id}`),
  simuleEquipement: (corps: Record<string, unknown>) =>
    ecrit<Simulation>('POST', '/api/equipements/simuler', corps),
  creeEquipement: (corps: Record<string, unknown>) =>
    ecrit<{ equipement: Equipement } & Application>('POST', '/api/equipements', corps),
  majEquipement: (id: string, corps: Record<string, unknown>) =>
    ecrit<{ equipement: Equipement }>('PATCH', `/api/equipements/${id}`, corps),
  regenereEquipement: (id: string) => ecrit<Application>('POST', `/api/equipements/${id}/regenerer`, {}),
  supprimeEquipement: (id: string) => ecrit<{ supprime: boolean }>('DELETE', `/api/equipements/${id}`),
  logements: () => lit<{ logements: Logement[] }>('/api/logements'),
  prestataires: () => lit<{ prestataires: Prestataire[] }>('/api/prestataires'),
  creePrestataire: (corps: Record<string, unknown>) =>
    ecrit<{ prestataire: Prestataire }>('POST', '/api/prestataires', corps),

  // ── Tâches ──────────────────────────────────────────────────────────────
  taches: (filtre: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtre)) if (valeur) params.set(cle, valeur);
    const requete = params.toString();
    return lit<{ taches: Tache[] }>(`/api/taches${requete ? `?${requete}` : ''}`);
  },
  tache: (id: string) =>
    lit<{ tache: Tache; historique: Realisation[]; documents: Document[] }>(`/api/taches/${id}`),
  creeTache: (corps: Record<string, unknown>) => ecrit<{ tache: Tache }>('POST', '/api/taches', corps),
  majTache: (id: string, corps: Record<string, unknown>) =>
    ecrit<{ tache: Tache }>('PATCH', `/api/taches/${id}`, corps),
  supprimeTache: (id: string) => ecrit<{ supprime: boolean }>('DELETE', `/api/taches/${id}`),

  /** Cochage : l'action la plus fréquente, et la seule qui doit marcher hors ligne. */
  coche: (id: string, titre: string, corps: Record<string, unknown> = {}) =>
    ecrit<{ tache: Tache; avertissements: string[] }>('POST', `/api/taches/${id}/fait`, corps, {
      differable: true,
      libelle: `Cocher « ${titre} »`,
    }),
  annuleCochage: (id: string, titre: string) =>
    ecrit<{ tache: Tache }>('DELETE', `/api/taches/${id}/fait`, undefined, {
      differable: true,
      libelle: `Annuler « ${titre} »`,
    }),

  // ── Calendrier scolaire ─────────────────────────────────────────────────
  vacances: () =>
    lit<{
      zone: string;
      periodes: PeriodeVacances[];
      enVacances: PeriodeVacances | null;
      etat: { dernierSucces: string | null; message: string | null } | null;
    }>('/api/vacances'),
  synchroniseVacances: () =>
    ecrit<{ periodes: number; etat: { dernierSucces: string | null } | null }>(
      'POST',
      '/api/vacances/synchroniser',
      {},
    ),
  simuleEcole: () => lit<Simulation>('/api/ecole/simuler'),
  genereEcole: (retenues?: string[]) =>
    ecrit<Application>('POST', '/api/ecole/generer', retenues ? { retenues } : {}),

  // ── Administratif ───────────────────────────────────────────────────────
  simuleAdministratif: () => ecrit<Simulation>('POST', '/api/administratif/simuler', {}),
  simuleModele: (cle: string, date: string, libelle?: string) =>
    ecrit<RegleDeduite>('POST', '/api/administratif/simuler-modele', { cle, date, libelle }),

  // ── Notifications ───────────────────────────────────────────────────────
  notifications: () =>
    lit<{
      heureResume: string | null;
      relanceRetard: Relance;
      annoncesActives: boolean;
      masqueAutres: boolean;
      silence: { actif: boolean; de: string; a: string };
      canauxDisponibles: { email: boolean; push: boolean; ntfy: boolean };
      appareils: { n: number };
      reglages: {
        categorie: string;
        canaux: Record<string, { actif: boolean; heureVoulue: number; heure: number; jourSemaine: number | null }>;
      }[];
    }>('/api/notifications'),
  majNotifications: (corps: Record<string, unknown>) =>
    ecrit<{ utilisateur: Utilisateur }>('PATCH', '/api/notifications', corps),
  majPreference: (corps: Record<string, unknown>) => ecrit('PUT', '/api/notifications/preferences', corps),
  majNtfy: (corps: Record<string, unknown>) => ecrit('PUT', '/api/notifications/ntfy', corps),
  testNotification: (canal: string) =>
    ecrit<{ envoye: boolean; message: string }>('POST', '/api/notifications/test', { canal }),
  clePush: () => lit<{ clePublique: string | null; pushDisponible: boolean }>('/api/notifications/cle-publique'),
  abonnePush: (corps: Record<string, unknown>) => ecrit('POST', '/api/notifications/abonnements', corps),

  /**
   * Report d'une tâche. `differable` : on reporte souvent le matin, parfois
   * sans réseau, et l'intention ne doit pas se perdre.
   */
  reporte: (id: string, jours: number) =>
    ecrit<{ reporteA: string | null }>('POST', `/api/taches/${id}/reporter`, { jours }, { differable: true }),
  reporteAu: (id: string, jusqua: string) =>
    ecrit<{ reporteA: string | null }>('POST', `/api/taches/${id}/reporter`, { jusqua }, { differable: true }),

  /**
   * Sourdine de relance sur une tâche. `differable` pour la même raison que le
   * report : le geste se fait au moment où la notification agace, réseau ou pas.
   */
  sourdine: (id: string, muette: boolean) =>
    ecrit<{ relanceMuette: boolean }>('POST', `/api/taches/${id}/sourdine`, { muette }, { differable: true }),

  // ── Liste de courses (façade Home Assistant) ────────────────────────────
  /**
   * `differable` sur les trois écritures : cocher un article dans le magasin,
   * là où le réseau est mauvais, doit marcher. La file locale du client HTTP et
   * la file `courses_outbox` du serveur se relaient — la première franchit la
   * coupure jusqu'au serveur, la seconde du serveur jusqu'à Home Assistant.
   */
  courses: () =>
    lit<{
      articles: {
        nom: string;
        coche: boolean;
        rayon: string | null;
        source: string | null;
        etat: 'synchronise' | 'en_attente';
      }[];
      enAttente: number;
      configure: boolean;
    }>('/api/courses'),
  synchroniseCourses: () =>
    ecrit<{
      articles: { nom: string; coche: boolean; source: string | null; etat: string }[];
      divergences: { nom: string; local: boolean; distant: boolean; explication: string }[];
      retires: string[];
      vidage: { transmises: number; abandonnees: { nom: string; raison: string }[]; bloquee: string | null };
    }>('POST', '/api/courses/synchroniser', {}),
  ajouteCourse: (nom: string, rayon?: string) =>
    ecrit('POST', '/api/courses', { nom, rayon }, { differable: true }),
  cocheCourse: (nom: string, coche: boolean) =>
    ecrit('PATCH', '/api/courses', { nom, coche }, { differable: true }),
  retireCourse: (nom: string) =>
    ecrit('POST', '/api/courses/retirer', { nom }, { differable: true }),
  ajouteAuxCourses: (tacheId: string, nom?: string) =>
    ecrit<{ ajoute: string | null; deja?: string; source?: string }>(
      'POST',
      `/api/taches/${tacheId}/courses`,
      nom ? { nom } : {},
    ),

  // ── Home Assistant ──────────────────────────────────────────────────────
  testeHa: (corps: { url?: string; jeton?: string }) =>
    ecrit<
      | { ok: true; version: string; entites: { id: string; nom: string; articles: number }[] }
      | { ok: false; cause: string; message: string }
    >('POST', '/api/ha/tester', corps),
  majHa: (corps: { url?: string | null; jeton?: string; entite?: string | null; entiteTaches?: string | null }) =>
    ecrit<{ famille: Famille }>('PUT', '/api/ha', corps),
  synchroniseTachesHa: () =>
    ecrit<
      | {
          ok: true;
          bilan: { cochees: number; ajoutees: number; modifiees: number; retirees: number; importees: number } | null;
          famille: Famille;
        }
      | { ok: false; cause: string; message: string; famille: Famille }
    >('POST', '/api/ha/taches/synchroniser', {}),

  // ── Export iCal et webhook ──────────────────────────────────────────────
  ical: () => lit<{ url: string; jeton: string }>('/api/ical'),
  regenereIcal: () =>
    ecrit<{ url: string; jeton: string; avertissement: string }>('POST', '/api/ical/regenerer', {}),
  webhook: () => lit<{ actif: boolean; url: string | null }>('/api/webhook'),
  activeWebhook: () =>
    ecrit<{ actif: boolean; url: string; exemple: Record<string, unknown> }>(
      'POST',
      '/api/webhook/activer',
      {},
    ),
  desactiveWebhook: () => ecrit<{ actif: boolean }>('POST', '/api/webhook/desactiver', {}),

  // ── Portabilité ─────────────────────────────────────────────────────────
  export: () => lit<Record<string, unknown>>('/api/export'),
};
